import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { textOn, visibleOnDark } from "./FusionShared";
import { money } from "./FusionShared";

const FLAT_PRESETS = [25, 50, 100, 250];
const RATE_PRESETS = [0.1, 0.25, 0.5, 1];

export function PledgeDialog({ open, onOpenChange, slug, athleteFirst, color: rawColor, onCommitted }) {
  const color = visibleOnDark(rawColor);
  const onColor = textOn(color);
  const [mode, setMode] = useState("per_foot");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [email2, setEmail2] = useState("");
  const [phone, setPhone] = useState("");
  const [flat, setFlat] = useState(50);
  const [rate, setRate] = useState(0.25);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!first.trim() || !last.trim()) return toast.error("Please enter your first and last name");
    if (!/^[\w.\-+]+@[\w.\-]+\.[a-zA-Z]{2,}$/.test(email)) return toast.error("A valid billing email is required");
    if (email.trim().toLowerCase() !== email2.trim().toLowerCase()) return toast.error("Email addresses don't match — please confirm your billing email");
    setBusy(true);
    try {
      const { data } = await api.post(`/fusion/public/athlete/${slug}/pledge`, {
        sponsor_first: first.trim(), sponsor_last: last.trim(),
        sponsor_email: email.trim(), sponsor_phone: phone.trim() || null,
        type: mode,
        flat_amount_usd: mode === "flat" ? Number(flat) : null,
        rate_usd_per_ft: mode === "per_foot" ? Number(rate) : null,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Pledge confirmed! Check your email for confirmation.");
      onOpenChange(false);
      onCommitted && onCommitted();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const chip = (active) =>
    `px-3 py-2 rounded-lg text-sm font-bold border transition-colors ${active ? "" : "text-white/70 border-white/15 bg-white/5 hover:bg-white/10"}`;
  const chipStyle = (active) => (active ? { background: color, borderColor: color, color: onColor } : {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1830] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="pledge-dialog">
        <DialogHeader>
          <DialogTitle>Support {athleteFirst}</DialogTitle>
          <DialogDescription className="text-white/60">
            Pledge per foot of {athleteFirst}'s best Hit-A-Thon distance, or donate a flat amount now.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <button className={chip(mode === "per_foot")} style={chipStyle(mode === "per_foot")} onClick={() => setMode("per_foot")} data-testid="pledge-mode-per-foot">
            Pledge per foot
          </button>
          <button className={chip(mode === "flat")} style={chipStyle(mode === "flat")} onClick={() => setMode("flat")} data-testid="pledge-mode-flat">
            Flat donation
          </button>
        </div>

        {mode === "per_foot" ? (
          <div className="space-y-2">
            <div className="text-xs text-white/50">You'll be invoiced <b className="text-white">rate × best verified distance</b> after the event. Example: $0.25/ft × 220 ft = $55.</div>
            <div className="flex gap-2 flex-wrap">
              {RATE_PRESETS.map((r) => (
                <button key={r} className={chip(rate === r)} style={chipStyle(rate === r)} onClick={() => setRate(r)} data-testid={`rate-preset-${r}`}>
                  ${r.toFixed(2)}/ft
                </button>
              ))}
            </div>
            <Input type="number" min="0.01" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
              className="bg-white/5 border-white/15 text-white" data-testid="pledge-rate-input" placeholder="Custom rate per foot" />
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2 flex-wrap">
              {FLAT_PRESETS.map((f) => (
                <button key={f} className={chip(flat === f)} style={chipStyle(flat === f)} onClick={() => setFlat(f)} data-testid={`flat-preset-${f}`}>
                  {money(f)}
                </button>
              ))}
            </div>
            <Input type="number" min="1" step="1" value={flat} onChange={(e) => setFlat(e.target.value)}
              className="bg-white/5 border-white/15 text-white" data-testid="pledge-flat-input" placeholder="Custom amount (USD)" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="sponsor-first-input" />
          <Input placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="sponsor-last-input" />
        </div>
        <Input type="email" placeholder="Billing email (invoice is sent here)" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="sponsor-email-input" />
        <Input type="email" placeholder="Confirm billing email" value={email2} onChange={(e) => setEmail2(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="sponsor-email-confirm-input" />
        <Input type="tel" placeholder="Mobile phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="sponsor-phone-input" />

        <Button onClick={submit} disabled={busy} className="w-full font-bold" style={{ background: color, color: onColor }} data-testid="pledge-submit-btn">
          {busy ? "Processing…" : mode === "flat" ? `Donate ${money(flat)} now` : `Pledge $${Number(rate).toFixed(2)} per foot`}
        </Button>
        <div className="text-[10px] text-white/40 text-center">
          {mode === "per_foot"
            ? "No charge today. After the athlete's five-swing result is verified, a secure Stripe invoice is emailed to your billing address."
            : "You'll be redirected to secure Stripe checkout."}
        </div>
      </DialogContent>
    </Dialog>
  );
}
